import types from './productConstants';

const INIT_STATE = {
    productList: [],
    listLoaded: false,
    listLoading: false,
    product: {},
    productNotFound: false,
    variants: [],
    images: [],
    pendingRowIds: [],
    pendingVariantIds: [],
    optionValues: [],
    categories: [],
    suppliers: [],
    productTypes: [],
    loading: false,
    success: null,
    error: null
};

const without = (ids, id) => (ids || []).filter(x => x !== id);
const withId = (ids, id) => [...without(ids, id), id];

const Product = (state = INIT_STATE, action) => {
    switch (action.type) {
        case types.CLEAR_STATE:
            return { ...INIT_STATE };

        // ---------- list ----------
        case types.GET_PRODUCTS:
            return { ...state, listLoading: true };
        case types.GET_PRODUCTS_SUCCESS:
            return {
                ...state,
                productList: (action.payload.data && action.payload.data.items) || [],
                listLoaded: true,
                listLoading: false,
                error: null
            };
        case types.GET_PRODUCTS_FAILED:
            return { ...state, listLoaded: true, listLoading: false, error: action.payload };

        // ---------- optimistic product flag ----------
        case types.PATCH_PRODUCT_ROW: {
            const { id, changes } = action.payload;
            return {
                ...state,
                productList: (state.productList || []).map(item => (item.id === id ? { ...item, ...changes } : item)),
                product: state.product && state.product.id === id ? { ...state.product, ...changes } : state.product,
                pendingRowIds: withId(state.pendingRowIds, id),
                success: null
            };
        }
        case types.PATCH_PRODUCT_ROW_SUCCESS: {
            const { id, success } = action.payload;
            return { ...state, pendingRowIds: without(state.pendingRowIds, id), success, error: null };
        }
        case types.PATCH_PRODUCT_ROW_FAILED: {
            const { id, previous, error } = action.payload;
            return {
                ...state,
                productList: (state.productList || []).map(item => (item.id === id ? { ...item, ...previous } : item)),
                product: state.product && state.product.id === id ? { ...state.product, ...previous } : state.product,
                pendingRowIds: without(state.pendingRowIds, id),
                error,
                success: null
            };
        }

        // ---------- product ----------
        case types.GET_PRODUCT:
            return { ...state, loading: true, productNotFound: false };
        case types.GET_PRODUCT_SUCCESS:
            return {
                ...state,
                product: action.payload.data,
                variants: action.payload.data.variants || [],
                images: action.payload.data.images || [],
                productNotFound: false,
                loading: false,
                error: null
            };
        case types.GET_PRODUCT_FAILED:
            return { ...state, product: {}, productNotFound: true, error: action.payload, loading: false };

        case types.POST_PRODUCT:
        case types.PATCH_PRODUCT:
        case types.POST_PRODUCT_CLONE:
            return { ...state, success: null };
        case types.POST_PRODUCT_SUCCESS:
            return { ...state, success: 'PRODUCT.CREATED.SUCESS', error: null };
        case types.PATCH_PRODUCT_SUCCESS:
            return { ...state, success: 'PRODUCT.UPDATED.SUCESS', error: null };
        case types.POST_PRODUCT_CLONE_SUCCESS:
            return { ...state, success: 'PRODUCT.CLONE.SUCESS', error: null };
        case types.POST_PRODUCT_FAILED:
        case types.PATCH_PRODUCT_FAILED:
        case types.POST_PRODUCT_CLONE_FAILED:
            return { ...state, success: null, error: action.payload };

        // ---------- images ----------
        case types.POST_PRODUCT_IMAGE_SUCCESS:
            return { ...state, success: 'PRODUCT.IMAGE.UPLOAD.SUCESS', error: null };
        case types.POST_PRODUCT_IMAGE_FAILED:
            return { ...state, success: null };
        case types.GET_PRODUCT_IMAGES:
        case types.PATCH_PRODUCT_IMAGES:
        case types.PATCH_PRODUCT_IMAGES_DONE:
        case types.DELETE_PRODUCT_IMAGE:
            return { ...state, success: null };
        case types.GET_PRODUCT_IMAGES_SUCCESS:
            return { ...state, images: action.payload || [], error: null };
        case types.GET_PRODUCT_IMAGES_FAILED:
        case types.DELETE_PRODUCT_IMAGE_FAILED:
            return { ...state, error: action.payload, success: null };
        case types.DELETE_PRODUCT_IMAGE_SUCCESS:
            return {
                ...state,
                images: (state.images || []).filter(s => s.id !== action.payload.id),
                success: 'PRODUCT.IMAGE.DELETED.SUCESS',
                error: null
            };

        // ---------- variants ----------
        case types.GET_VARIANTS:
        case types.POST_VARIANT:
        case types.PATCH_VARIANT:
            return { ...state, success: null };
        case types.GET_VARIANTS_SUCCESS:
            return { ...state, variants: action.payload.data || [], error: null };
        case types.GET_VARIANTS_FAILED:
        case types.POST_VARIANT_FAILED:
        case types.PATCH_VARIANT_FAILED:
            return { ...state, error: action.payload, success: null };
        case types.POST_VARIANT_SUCCESS:
            return { ...state, success: 'VARIANT.CREATED.SUCESS', error: null };
        case types.PATCH_VARIANT_SUCCESS:
            return { ...state, success: 'VARIANT.UPDATED.SUCESS', error: null };

        case types.PATCH_VARIANT_ROW: {
            const { id, changes } = action.payload;
            return {
                ...state,
                variants: (state.variants || []).map(v => (v.id === id ? { ...v, ...changes } : v)),
                pendingVariantIds: withId(state.pendingVariantIds, id),
                success: null
            };
        }
        case types.PATCH_VARIANT_ROW_SUCCESS: {
            const { id, success } = action.payload;
            return { ...state, pendingVariantIds: without(state.pendingVariantIds, id), success, error: null };
        }
        case types.PATCH_VARIANT_ROW_FAILED: {
            const { id, previous, error } = action.payload;
            return {
                ...state,
                variants: (state.variants || []).map(v => (v.id === id ? { ...v, ...previous } : v)),
                pendingVariantIds: without(state.pendingVariantIds, id),
                error,
                success: null
            };
        }

        // ---------- lookups ----------
        case types.GET_OPTION_VALUES:
            return { ...state, optionValues: (action.payload && action.payload.data) || [] };
        case types.GET_CATEGORIES:
            return { ...state, categories: (action.payload && action.payload.data) || [] };
        case types.GET_SUPPLIERS:
            return { ...state, suppliers: (action.payload && action.payload.data && action.payload.data.items) || [] };
        case types.GET_LOOKUP_VALUES:
            return { ...state, productTypes: (action.payload && action.payload.data) || [] };
        default:
            return state;
    }
};

export default Product;
